import { useState, useEffect, useMemo, useCallback } from "react";
import { FiCheck, FiX } from "react-icons/fi";
import { Button } from "newui/components/Button";
import { SelectWithLabel } from "newui/components/ui/InputWithLabel/SelectWithLabel";
import type { SelectOption } from "newui/components/ui/InputWithLabel/SelectWithLabel";
import { CompactSelect } from "newui/components/ui/InputWithLabel/CompactSelect";
import { CompactTextInput } from "newui/components/ui/InputWithLabel/CompactTextInput";
import { cn } from "utils/helpers";
import { useDebounceFunction } from "newui/hooks/useDebounceFunction";
import type {
	AddPanelMemberPayload,
	CompanyUser,
	EvaluationPanelMember,
	PanelMode,
} from "../../../types/panel.type";
import { searchCompanyUsers, getAiPanelUsers } from "../../../api/panel-api";

type AddMemberType = "internal" | "external" | "ai";

interface AddPanelMemberRowProps {
	companyId: number | null;
	currentUserId: number | null;
	panelMembers: EvaluationPanelMember[];
	panelMode: PanelMode;
	rfxId: number;
	onAddMember: (payload: AddPanelMemberPayload) => Promise<void>;
	onClose: () => void;
}

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<span className="mb-1 block text-xs font-medium text-gray-600">
		{children} <span className="text-red-500">*</span>
	</span>
);

export const AddPanelMemberRow: React.FC<AddPanelMemberRowProps> = ({
	companyId,
	currentUserId,
	panelMembers,
	panelMode,
	rfxId,
	onAddMember,
	onClose,
}) => {
	const showAi = panelMode === "ai_panel";

	const [memberType, setMemberType] = useState<AddMemberType>("internal");
	const [isAdding, setIsAdding] = useState(false);

	// Internal (company user) — searchable async select
	const [companyUsers, setCompanyUsers] = useState<SelectOption[]>([]);
	const [companyUserEmailMap, setCompanyUserEmailMap] = useState<Map<number, string>>(new Map());
	const [isSearchingUsers, setIsSearchingUsers] = useState(false);
	const [selectedCompanyUser, setSelectedCompanyUser] = useState<SelectOption | null>(null);

	const [externalEmail, setExternalEmail] = useState("");
	const [externalFirstName, setExternalFirstName] = useState("");
	const [externalLastName, setExternalLastName] = useState("");
	const [emailError, setEmailError] = useState<string | null>(null);

	const [aiUsers, setAiUsers] = useState<{ id: number; name: string; description: string }[]>([]);
	const [selectedAiUserId, setSelectedAiUserId] = useState<number | null>(null);

	const addedEmails = useMemo(
		() => new Set(panelMembers.map((m) => m.email.toLowerCase())),
		[panelMembers]
	);

	const addedAiUserIds = useMemo(
		() => new Set(
			panelMembers
				.filter((m) => m.member_type === "ai" && m.ai_panel_user)
				.map((m) => m.ai_panel_user!.id)
		),
		[panelMembers]
	);

	const updateCompanyUsers = useCallback((users: CompanyUser[]): void => {
		setCompanyUsers(users.map((u) => ({ label: `${u.full_name} (${u.email})`, value: u.id })));
		const emailMap = new Map<number, string>();
		users.forEach((u) => emailMap.set(u.id, u.email.toLowerCase()));
		setCompanyUserEmailMap(emailMap);
	}, []);

	useEffect(() => {
		if (!companyId) {
			return;
		}
		setIsSearchingUsers(true);
		searchCompanyUsers(companyId, "")
			.then(updateCompanyUsers)
			.catch(() => setCompanyUsers([]))
			.finally(() => setIsSearchingUsers(false));
	}, [companyId, updateCompanyUsers]);

	useEffect(() => {
		if (!showAi) {
			return;
		}
		getAiPanelUsers(rfxId)
			.then((users) => setAiUsers(users.filter((u) => u.is_active)))
			.catch(() => setAiUsers([]));
	}, [showAi, rfxId]);

	const availableCompanyUsers = useMemo(
		() => companyUsers.filter((u) => {
			if (currentUserId !== null && u.value === currentUserId) {
				return false;
			}
			const email = companyUserEmailMap.get(u.value as number);
			return !email || !addedEmails.has(email);
		}),
		[companyUsers, companyUserEmailMap, addedEmails, currentUserId]
	);

	const aiUserOptions = useMemo(
		() => aiUsers
			.filter((u) => !addedAiUserIds.has(u.id))
			.map((u) => ({ value: u.id, label: `${u.name} — ${u.description}` })),
		[aiUsers, addedAiUserIds]
	);

	const debouncedSearch = useDebounceFunction(async (query: string) => {
		if (!companyId) {
			setCompanyUsers([]);
			setIsSearchingUsers(false);
			return;
		}
		try {
			const users = await searchCompanyUsers(companyId, query);
			updateCompanyUsers(users);
		} catch {
			setCompanyUsers([]);
		} finally {
			setIsSearchingUsers(false);
		}
	}, 300);

	const handleCompanyUserSearch = (inputValue: string): void => {
		setIsSearchingUsers(true);
		debouncedSearch(inputValue);
	};

	const canSave = (() => {
		if (memberType === "internal") {
			return selectedCompanyUser !== null;
		}
		if (memberType === "external") {
			return (
				externalEmail.trim() !== "" &&
				externalFirstName.trim() !== "" &&
				externalLastName.trim() !== ""
			);
		}
		return selectedAiUserId !== null;
	})();

	const handleSave = async (): Promise<void> => {
		if (!canSave || isAdding) {
			return;
		}

		let payload: AddPanelMemberPayload | null = null;

		if (memberType === "internal" && selectedCompanyUser) {
			payload = { type: "internal", user_id: selectedCompanyUser.value as number };
		} else if (memberType === "external") {
			const email = externalEmail.trim();
			if (addedEmails.has(email.toLowerCase())) {
				setEmailError("This email has already been added to the panel.");
				return;
			}
			payload = {
				type: "external",
				email,
				name: `${externalFirstName.trim()} ${externalLastName.trim()}`.trim(),
			};
		} else if (memberType === "ai" && selectedAiUserId !== null) {
			payload = { type: "ai", ai_panel_user_id: selectedAiUserId };
		}

		if (!payload) {
			return;
		}

		setIsAdding(true);
		try {
			await onAddMember(payload);
			onClose();
		} finally {
			setIsAdding(false);
		}
	};

	const toggleOptions: Array<{ value: AddMemberType; label: string }> = [
		{ value: "internal", label: "Internal" },
		{ value: "external", label: "External" },
		...(showAi ? [{ value: "ai" as AddMemberType, label: "AI" }] : []),
	];

	return (
		<div
			className="w-full rounded-lg border border-gray-200 bg-gray-50 p-4 md:w-1/2"
			data-testid="add-panel-member-row"
		>
			<div className="flex items-center justify-between">
				<div className="inline-flex overflow-hidden rounded-md border border-gray-300" role="tablist" aria-label="Member type">
					{toggleOptions.map((opt) => (
						<button
							key={opt.value}
							type="button"
							role="tab"
							aria-selected={memberType === opt.value}
							onClick={() => setMemberType(opt.value)}
							className={cn(
								"px-4 py-1.5 text-sm font-medium transition-colors",
								memberType === opt.value
									? "bg-[#0031DD] text-white"
									: "bg-white text-gray-600 hover:bg-gray-50"
							)}
							data-testid={`add-member-type-${opt.value}`}
						>
							{opt.label}
						</button>
					))}
				</div>
				<button
					type="button"
					onClick={onClose}
					className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
					aria-label="Cancel adding member"
					data-testid="add-panel-member-cancel"
				>
					<FiX size={18} />
				</button>
			</div>

			<div className="mt-4 space-y-3">
				{memberType === "internal" && (
					<SelectWithLabel
						name="add-company-user"
						label="Company user"
						required
						placeholder="Search by name or email…"
						options={availableCompanyUsers}
						value={selectedCompanyUser}
						onChange={(option) => setSelectedCompanyUser(option as SelectOption | null)}
						onInputChange={handleCompanyUserSearch}
						isLoading={isSearchingUsers}
						testId="add-company-user-select"
						clearAble
					/>
				)}

				{memberType === "external" && (
					<>
						<div>
							<FieldLabel>Email</FieldLabel>
							<CompactTextInput
								type="email"
								required
								value={externalEmail}
								onChange={(e) => {
									setExternalEmail(e.target.value);
									if (emailError) {
										setEmailError(null);
									}
								}}
								placeholder="email@example.com"
								className="w-full"
								hasError={!!emailError}
								data-testid="add-external-email-input"
							/>
							{emailError && (
								<p className="mt-1 text-xs text-red-600" data-testid="add-external-email-error">{emailError}</p>
							)}
						</div>
						<div>
							<FieldLabel>First name</FieldLabel>
							<CompactTextInput
								required
								value={externalFirstName}
								onChange={(e) => setExternalFirstName(e.target.value)}
								placeholder="First name"
								className="w-full"
								data-testid="add-external-first-name-input"
							/>
						</div>
						<div>
							<FieldLabel>Last name</FieldLabel>
							<CompactTextInput
								required
								value={externalLastName}
								onChange={(e) => setExternalLastName(e.target.value)}
								placeholder="Last name"
								className="w-full"
								data-testid="add-external-last-name-input"
							/>
						</div>
					</>
				)}

				{memberType === "ai" && (
					<div>
						<FieldLabel>AI persona</FieldLabel>
						<CompactSelect
							required
							value={selectedAiUserId ?? ""}
							onChange={(e) => setSelectedAiUserId(e.target.value ? Number(e.target.value) : null)}
							placeholder="Select an AI persona…"
							options={aiUserOptions}
							className="w-full"
							data-testid="add-ai-user-select"
						/>
					</div>
				)}
			</div>

			<div className="mt-4 flex justify-end">
				<Button
					btntype="primary"
					size="sm"
					onClick={handleSave}
					disabled={!canSave || isAdding}
					isLoading={isAdding}
					data-testid="add-panel-member-save"
				>
					<span className="flex items-center gap-1">
						<FiCheck size={14} />
						Save
					</span>
				</Button>
			</div>
		</div>
	);
};
